<template>
  <modal
    name="transfer-inventory-modal"
    width="1200"
    height="auto"
    :adaptive="true"
    :scrollable="true"
  >
    <div class="py-6">
      <div class="flex justify-between items-center mb-4 px-6">
        <h2 class="text-xl font-bold">Transfer Geçmişi</h2>
        <!-- <button
          @click="openNewTransferModal"
          class="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center"
        >
          <i class="fas fa-plus mr-2"></i>
          Yeni Transfer
        </button> -->
      </div>

      <div v-if="inventory" class="mb-4 mx-6 p-3 bg-gray-100 rounded">
        <p class="text-sm text-gray-600">Ekipman:</p>
        <p class="font-bold">{{ inventory.name }}</p>
        <p class="text-sm text-gray-600">Ürün Kodu: {{ inventory.product_code }}</p>
      </div>

      <div class="px-6">
        <!-- Loading State -->
        <div v-if="loading" class="flex justify-center items-center py-12">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <p class="text-red-800">{{ error }}</p>
        </div>

        <!-- Empty State -->
        <div v-else-if="transferHistory.length === 0" class="text-center py-12 text-gray-500">
          <i class="fas fa-inbox text-5xl mb-4"></i>
          <p>Henüz transfer geçmişi bulunmamaktadır</p>
        </div>

        <!-- Transfer History Table -->
        <div v-else class="overflow-x-auto max-h-96">
          <table class="w-full">
            <thead class="bg-gray-50 sticky top-0">
              <tr>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tarih</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Gönderen</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Alıcı</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sebep</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Transfer Fotoğrafı</th>
                <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Not</th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <tr v-for="item in transferHistory" :key="item.id" class="hover:bg-gray-50">
                <td class="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                  {{ formatDate(item.transferDate) }}
                </td>
                <td class="px-4 py-3 text-sm">
                  <div class="font-medium text-gray-900">{{ item.from.plate || '-' }}</div>
                  <div class="text-xs text-gray-500">{{ item.from.type }}</div>
                </td>
                <td class="px-4 py-3 text-sm">
                  <div class="font-medium text-gray-900">{{ item.to.plate || '-' }}</div>
                  <div class="text-xs text-gray-500">{{ item.to.type }}</div>
                </td>
                <td class="px-4 py-3 text-sm text-gray-900">
                  <span class="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                    {{ item.reason || '-' }}
                  </span>
                </td>
                <td class="px-4 py-3 text-sm">
                  <div v-if="item.transferImage" class="flex items-center space-x-2">
                    <button
                      @click="viewImage(item.transferImage)"
                      class="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors flex items-center space-x-1"
                      :title="`Fotoğrafı Görüntüle\n${item.transferImage}`"
                    >
                      <i class="fas fa-image"></i>
                      <span>Görüntüle</span>
                    </button>
                    <button
                      @click="copyImageUrl(item.transferImage)"
                      class="px-2 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
                      :title="`URL'yi Kopyala\n${item.transferImage}`"
                    >
                      <i class="fas fa-copy"></i>
                    </button>
                  </div>
                  <span v-else class="text-gray-400">-</span>
                </td>
                <td class="px-4 py-3 text-sm text-gray-600">
                  <span v-if="item.notes" :title="item.notes">
                    {{ truncateText(item.notes, 30) }}
                  </span>
                  <span v-else class="text-gray-400">-</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div v-if="pagination.pages > 1" class="mt-4 flex justify-between items-center">
          <div class="text-sm text-gray-700">
            Toplam {{ pagination.total }} kayıt
          </div>
          <div class="flex space-x-2">
            <button
              v-for="page in visiblePages"
              :key="page"
              @click="changePage(page)"
              :class="[
                'px-3 py-1 rounded text-sm',
                page === pagination.page + 1
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              ]"
            >
              {{ page }}
            </button>
          </div>
        </div>

        <!-- Close Button -->
        <div class="flex justify-end mt-6">
          <button
            type="button"
            @click="$modal.hide('transfer-inventory-modal')"
            class="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            Kapat
          </button>
        </div>
      </div>
    </div>

    <!-- Image Preview Modal -->
    <modal
      name="image-preview-modal"
      width="800"
      height="auto"
      :adaptive="true"
    >
      <div class="p-6">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-bold">Transfer Fotoğrafı</h3>
          <button
            @click="$modal.hide('image-preview-modal')"
            class="text-gray-500 hover:text-gray-700"
          >
            <i class="fas fa-times text-xl"></i>
          </button>
        </div>
        <img
          v-if="selectedImage"
          :src="selectedImage"
          class="w-full h-auto rounded-lg"
          alt="Transfer Fotoğrafı"
          @error="handleImageError"
        />
        <div v-else class="text-center text-gray-500 py-8">
          <i class="fas fa-image text-4xl mb-2"></i>
          <p>Fotoğraf bulunamadı</p>
        </div>
      </div>
    </modal>

    <!-- New Transfer Modal -->
    <newTransferModal :inventory="inventory" @refresh="onTransferComplete" />
  </modal>
</template>

<script>
import axios from 'axios';
import { inventory, general } from '../../../../networking/urlmanager';
import moment from 'moment';
import newTransferModal from './newTransferModal.vue';

export default {
  name: 'TransferHistoryModal',
  components: {
    newTransferModal
  },
  props: {
    inventory: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      error: null,
      transferHistory: [],
      selectedImage: null,
      pagination: {
        page: 0,
        limit: 20,
        total: 0,
        pages: 0
      }
    };
  },
  computed: {
    visiblePages() {
      const pages = [];
      const currentPage = this.pagination.page + 1;
      const start = Math.max(1, currentPage - 2);
      const end = Math.min(this.pagination.pages, currentPage + 2);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      return pages;
    }
  },
  watch: {
    inventory: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.id) {
          this.pagination.page = 0;
          this.fetchTransferHistory();
        }
      }
    }
  },
  methods: {
    async fetchTransferHistory() {
      if (!this.inventory || !this.inventory.id) return;

      this.loading = true;
      this.error = null;

      try {
        const response = await axios.get(inventory.transferHistory, {
          params: {
            inventoryId: this.inventory.id,
            page: this.pagination.page,
            limit: this.pagination.limit
          },
          headers: {
            Authorization: "Bareer " + this.$store.state.userData.token,
          }
        });

        if (response.data.code === 200) {
          this.transferHistory = response.data.data.detail || [];
          this.pagination = {
            total: response.data.data.totalCount,
            pages: response.data.data.pageCount,
            page: this.pagination.page,
            limit: this.pagination.limit
          };
        }
      } catch (error) {
        console.error('Error fetching transfer history:', error);
        this.error = error.response?.data?.message || 'Transfer geçmişi alınırken hata oluştu';
      } finally {
        this.loading = false;
      }
    },

    changePage(displayPage) {
      this.pagination.page = displayPage - 1;
      this.fetchTransferHistory();
    },

    formatDate(dateString) {
      return moment(dateString).format('DD.MM.YYYY HH:mm');
    },

    truncateText(text, length) {
      if (!text) return '';
      return text.length > length ? text.substring(0, length) + '...' : text;
    },

    async viewImage(fileName) {
      if (!fileName) {
        this.$toast.error('Fotoğraf bulunamadı');
        return;
      }

      try {
        // Backend'den signed URL al (Google Cloud Storage)
        const response = await axios.get(
          `${general.getFileUrl}?url=${encodeURIComponent(fileName)}`,
          {
            headers: {
              Authorization: "Bareer " + this.$store.state.userData.token,
            }
          }
        );

        if (response.data.code === 200) {
          this.selectedImage = response.data.data;
          this.$modal.show('image-preview-modal');
        } else {
          throw new Error(response.data.message || 'URL alınamadı');
        }
      } catch (error) {
        console.error('Signed URL alınamadı:', error);
        this.$toast.error('Fotoğraf URL\'i alınamadı: ' + (error.response?.data?.message || error.message));
      }
    },

    async copyImageUrl(imageUrl) {
      try {
        await navigator.clipboard.writeText(imageUrl);
        this.$toast.success('URL kopyalandı');
      } catch (error) {
        console.error('URL kopyalama hatası:', error);
        this.$toast.error('URL kopyalanamadı');
      }
    },

    openNewTransferModal() {
      this.$modal.show('new-transfer-modal');
    },

    onTransferComplete() {
      // Transfer tamamlandığında geçmişi yenile
      this.fetchTransferHistory();
      // Ana inventory listesini de yenile
      this.$emit('refresh');
    },

    handleImageError(event) {
      console.error('Fotoğraf yüklenemedi:', event.target.src);
      this.$toast.error('Fotoğraf yüklenemedi. Dosya bulunamıyor veya erişilemiyor.');
    }
  }
};
</script>
